import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid,
	Paper,
	Tooltip,
	Typography,
} from '@mui/material';
import FloatingCompanySelector from 'containers/pages/CANparkAdmin/components/floatingCompanySelector/FloatingCompanySelector';
import { AddIcon } from 'components/icons';
import { useCustomers } from 'containers/pages/CANpark/hooks/useCustomers';
import { useCANparkAdminAppAccessControl } from 'containers/pages/CANparkAdmin/hooks/useCANparkAdminAppAccessControl';
import ParkingLocationDrawer from 'containers/pages/CANparkAdmin/components/parkingLocationDrawer/ParkingLocationDrawer';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { useParkingLocations } from 'containers/pages/CANpark/hooks/useParkingLocations';
import ParkingLocationCard from 'containers/pages/CANparkAdmin/components/ParkingLocationCard';
import ParkingLocationAdminView from 'containers/pages/CANparkAdmin/components/ParkingLocationAdminView';
import { initParkingLocation } from 'containers/pages/CANparkAdmin/data/initParkingLocation';
import {
	readCanparkAdminLastSelection,
	writeCanparkAdminLastSelection,
} from 'containers/pages/CANparkAdmin/utils/canparkAdminLastSelection';
import { useManageSitesTourSync } from 'components/onboarding/tour/useManageSitesTourSync';
import { useMonitorSiteTourSync } from 'components/onboarding/tour/useMonitorSiteTourSync';
import { useRegisterPlateTourSync } from 'components/onboarding/tour/useRegisterPlateTourSync';

function CANparkAdmin() {
	const { isAdmin, assignedCustomer } = useCANparkAdminAppAccessControl();

	const { customers, loading: loadingCompanies } = useCustomers();

	const [editParkingLocation, setEditParkingLocation] =
		React.useState<ParkingLocation | null>(null);

	// Drives the Manage Sites tour's action gates (page arrival + drawer open).
	useManageSitesTourSync(editParkingLocation !== null);
	const [activeParkingLocation, setActiveParkingLocation] =
		useState<ParkingLocation | null>(null);
	// Drives the Monitor Site tour's action gates (page arrival + View opened).
	useMonitorSiteTourSync(activeParkingLocation !== null);
	// Drives the Register Plate tour's action gates (page arrival, form open, save).
	useRegisterPlateTourSync();
	const [selectedCustomer, setSelectedCustomer] = useState<{
		Id: string;
		Name: string;
	} | null>(null);

	const {
		parkingLocations,
		setParkingLocations,
		loading: loadingParkingLocations,
	} = useParkingLocations(selectedCustomer?.Id || null);

	// CANparkAdmin or CANparkClient
	useEffect(() => {
		if (isAdmin && customers && customers.length > 0) {
			const cached = readCanparkAdminLastSelection();
			const match = cached.customerId
				? customers.find((c) => c.Id === cached.customerId)
				: undefined;
			setSelectedCustomer(match || customers[0]);
		} else if (customers && customers.length > 0) {
			// If user is not admin, then find the customer or company that is assigned to the user
			const selected = customers.find((c) => c.Id === assignedCustomer);
			if (selected) setSelectedCustomer(selected);
		}
	}, [isAdmin, customers, assignedCustomer]);

	useEffect(() => {
		if (isAdmin && selectedCustomer?.Id) {
			writeCanparkAdminLastSelection({ customerId: selectedCustomer.Id });
		}
	}, [isAdmin, selectedCustomer?.Id]);

	return (
		<Grid
			container
			style={{
				display: 'flex',
				justifyContent: 'center',
			}}
		>
			{activeParkingLocation ? (
				<>
					<ParkingLocationAdminView
						activeParkingLocation={activeParkingLocation}
						onBack={() => setActiveParkingLocation(null)}
					/>
				</>
			) : (
				<>
					<Box sx={{ width: '100%', p: 2 }}>
						<Paper sx={{ p: 2, mb: 2 }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: { xs: 'flex-start', sm: 'center' },
									justifyContent: 'space-between',
									flexDirection: { xs: 'column', sm: 'row' },
									gap: 2,
								}}
							>
								<Box>
									<Typography variant="h5" fontWeight={400}>
										Parking locations
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										{selectedCustomer?.Name || ' '}
									</Typography>
								</Box>

								{isAdmin && (
									<Tooltip
										title={
											selectedCustomer
												? ''
												: 'Select a customer first'
										}
									>
										<span>
											<Button
												data-tour="add-parking-site-button"
												startIcon={<AddIcon />}
												onClick={() => {
													if (!selectedCustomer) return;
													setEditParkingLocation({
														...initParkingLocation,
														customerID:
															selectedCustomer.Id,
													});
												}}
												disabled={!selectedCustomer}
												variant="outlined"
												sx={{ borderRadius: '999px' }}
												size="small"
												color="success"
											>
												Add Parking Site
											</Button>
										</span>
									</Tooltip>
								)}
							</Box>

							{isAdmin && (
								<>
									<Divider sx={{ my: 2 }} />
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1.5,
											flexWrap: 'wrap',
										}}
									>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{
												textTransform: 'uppercase',
												letterSpacing: 0.8,
												fontWeight: 600,
											}}
										>
											Customer
										</Typography>
										<Box
											data-tour="company-selector"
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 1,
												pl: 2,
												pr: 0.5,
												py: 0.25,
												border: 2,
												borderColor: 'primary.main',
												borderRadius: '999px',
												minWidth: 240,
												bgcolor: 'background.paper',
												transition: (theme) =>
													theme.transitions.create([
														'border-color',
														'background-color',
													]),
												'&:hover': {
													borderColor: 'primary.dark',
													bgcolor: 'action.hover',
												},
											}}
										>
											<Typography
												variant="body1"
												fontWeight={500}
												sx={{
													flex: 1,
													color: selectedCustomer
														? 'text.primary'
														: 'text.disabled',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
											>
												{selectedCustomer?.Name ||
													'Select a customer'}
											</Typography>
											<FloatingCompanySelector
												onUpdate={(customer) =>
													setSelectedCustomer(customer)
												}
												companies={customers || []}
												loadingCompanies={loadingCompanies}
											/>
										</Box>
									</Box>
								</>
							)}
						</Paper>
					</Box>
					<Grid
						style={{
							width: '100%',
							padding: '1rem',
							display: 'flex',
							flexWrap: 'wrap',
							gap: '1rem',
							justifyContent: 'center',
							alignItems: 'center',
						}}
					>
						{loadingParkingLocations ? (
							<CircularProgress />
						) : (
							<>
								{parkingLocations.length === 0 && (
									<Typography
										variant="h6"
										color="warning.main"
										fontStyle="italic"
										fontWeight={300}
									>
										No parking locations found
									</Typography>
								)}
								{parkingLocations.map((location) => (
									<ParkingLocationCard
										key={location.id}
										parkingLocation={location}
										onClickEdit={(location) => {
											setEditParkingLocation(location);
										}}
										onClickView={(location) => {
											setActiveParkingLocation(location);
										}}
									/>
								))}
							</>
						)}
					</Grid>
				</>
			)}
			{editParkingLocation && (
				<ParkingLocationDrawer
					onClose={() => setEditParkingLocation(null)}
					selectedParkingLocation={editParkingLocation}
					onSave={(updatedLocations) => {
						setParkingLocations(updatedLocations);
						setActiveParkingLocation(null);
					}}
				/>
			)}
		</Grid>
	);
}

export default CANparkAdmin;
